import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { AlertTriangle, ChevronRight, MessageCircle, IndianRupee, CheckCircle2 } from "lucide-react";
import useJsonLd from "../hooks/useJsonLd";
import useCanonical from "../hooks/useCanonical";

const WHATSAPP_URL = "https://wa.me/918940988776?text=Hi%2C%20I%20used%20the%20income%20tax%20calculator%20and%20need%20tax%20planning%20advice";

const formatINR = (num) => Math.round(num || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

// ---------- Slab calculators ----------

// FY 2024-25 New Regime slabs
const calcNewRegime2024 = (taxable) => {
  let tax = 0;
  const slabs = [
    [300000, 0],
    [400000, 0.05],   // 3L to 7L
    [300000, 0.10],   // 7L to 10L
    [200000, 0.15],   // 10L to 12L
    [300000, 0.20],   // 12L to 15L
    [Infinity, 0.30], // 15L+
  ];
  let remaining = taxable;
  for (const [slab, rate] of slabs) {
    if (remaining <= 0) break;
    const taxableInSlab = Math.min(remaining, slab);
    tax += taxableInSlab * rate;
    remaining -= taxableInSlab;
  }
  return tax;
};

// FY 2025-26 New Regime slabs (Budget 2025)
const calcNewRegime2025 = (taxable) => {
  let tax = 0;
  const slabs = [
    [400000, 0],
    [400000, 0.05],   // 4L to 8L
    [400000, 0.10],   // 8L to 12L
    [400000, 0.15],   // 12L to 16L
    [400000, 0.20],   // 16L to 20L
    [400000, 0.25],   // 20L to 24L
    [Infinity, 0.30], // 24L+
  ];
  let remaining = taxable;
  for (const [slab, rate] of slabs) {
    if (remaining <= 0) break;
    const taxableInSlab = Math.min(remaining, slab);
    tax += taxableInSlab * rate;
    remaining -= taxableInSlab;
  }
  return tax;
};

// Old regime (same across FY 2024-25 and FY 2025-26) with age-based basic exemption
const calcOldRegime = (taxable, ageGroup) => {
  let basicExempt = 250000;
  if (ageGroup === "SENIOR") basicExempt = 300000;
  if (ageGroup === "SUPER_SENIOR") basicExempt = 500000;

  let tax = 0;
  let remaining = taxable;

  // Basic exemption
  const exempt = Math.min(remaining, basicExempt);
  remaining -= exempt;

  // 5% slab (up to 5L for super senior it's already covered; for others up to 5L)
  if (ageGroup !== "SUPER_SENIOR") {
    const slab5Upper = 500000 - basicExempt;
    const inSlab = Math.min(remaining, slab5Upper);
    tax += inSlab * 0.05;
    remaining -= inSlab;
  }

  // 20% slab up to 10L
  const slab20Upper = ageGroup === "SUPER_SENIOR" ? 500000 : 500000;
  const inSlab20 = Math.min(remaining, slab20Upper);
  tax += inSlab20 * 0.20;
  remaining -= inSlab20;

  // 30% slab above 10L
  tax += Math.max(0, remaining) * 0.30;

  return tax;
};

// Surcharge (simplified — capped at 25% above 2 Cr)
const applySurcharge = (tax, income) => {
  let rate = 0;
  if (income > 20000000) rate = 0.25;
  else if (income > 10000000) rate = 0.15;
  else if (income > 5000000) rate = 0.10;
  return tax * (1 + rate);
};

const calcRegime = ({ fy, regime, inputs }) => {
  const {
    employmentType,
    grossIncome,
    ageGroup,
    employerNps,
    sec80c,
    sec80d,
    hra,
    homeLoanInterest,
    otherDeductions,
  } = inputs;

  const isSalaried = employmentType === "SALARIED";

  let deductions = 0;
  let standardDeduction = 0;

  if (regime === "NEW") {
    standardDeduction = isSalaried ? 75000 : 0;
    deductions = standardDeduction + (Number(employerNps) || 0);
  } else {
    // OLD regime
    standardDeduction = isSalaried ? 50000 : 0;
    const c80c = Math.min(Number(sec80c) || 0, 150000);
    const maxHealth = ageGroup === "SENIOR" || ageGroup === "SUPER_SENIOR" ? 50000 : 25000;
    const c80d = Math.min(Number(sec80d) || 0, maxHealth);
    const c24b = Math.min(Number(homeLoanInterest) || 0, 200000);
    deductions =
      standardDeduction +
      c80c +
      c80d +
      (Number(hra) || 0) +
      c24b +
      (Number(otherDeductions) || 0) +
      (Number(employerNps) || 0);
  }

  const taxableIncome = Math.max(0, (Number(grossIncome) || 0) - deductions);

  let baseTax = 0;
  if (regime === "NEW") {
    baseTax = fy === "FY2025-26" ? calcNewRegime2025(taxableIncome) : calcNewRegime2024(taxableIncome);
  } else {
    baseTax = calcOldRegime(taxableIncome, ageGroup);
  }

  // Surcharge
  const taxAfterSurcharge = applySurcharge(baseTax, taxableIncome);

  // Rebate u/s 87A
  let rebate = 0;
  if (regime === "NEW") {
    if (fy === "FY2025-26") {
      if (taxableIncome <= 1200000) {
        rebate = Math.min(taxAfterSurcharge, 60000);
      }
    } else {
      // FY2024-25 new regime: zero tax up to 7L, rebate up to 25,000
      if (taxableIncome <= 700000) {
        rebate = Math.min(taxAfterSurcharge, 25000);
      }
    }
  } else {
    // Old regime: rebate up to 12,500 if taxable income <= 5L
    if (taxableIncome <= 500000) {
      rebate = Math.min(taxAfterSurcharge, 12500);
    }
  }

  const taxAfterRebate = Math.max(0, taxAfterSurcharge - rebate);
  const cess = taxAfterRebate * 0.04;
  const totalTax = taxAfterRebate + cess;

  return {
    grossIncome: Number(grossIncome) || 0,
    deductions,
    taxableIncome,
    baseTax: taxAfterSurcharge,
    rebate,
    cess,
    totalTax,
  };
};

const IncomeTaxCalculatorPage = () => {
  const [fy, setFy] = useState("FY2025-26");
  const [employmentType, setEmploymentType] = useState("SALARIED");
  const [grossIncome, setGrossIncome] = useState(1500000);
  const [ageGroup, setAgeGroup] = useState("BELOW_60");
  const [employerNps, setEmployerNps] = useState(0);

  // Old regime specific
  const [sec80c, setSec80c] = useState(150000);
  const [sec80d, setSec80d] = useState(25000);
  const [hra, setHra] = useState(0);
  const [homeLoanInterest, setHomeLoanInterest] = useState(0);
  const [otherDeductions, setOtherDeductions] = useState(0);

  useCanonical("/income-tax-calculator");
  useJsonLd({
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "Income Tax Calculator — Old vs New Regime",
    "applicationCategory": "FinanceApplication",
    "operatingSystem": "All",
    "description": "Free income tax calculator for FY 2024-25 and FY 2025-26. Compare Old vs New tax regime, including Budget 2025 slabs.",
    "url": "https://wintrustsolutions.com/income-tax-calculator",
    "offers": { "@type": "Offer", "price": "0", "priceCurrency": "INR" },
  });

  const inputs = {
    employmentType,
    grossIncome,
    ageGroup,
    employerNps,
    sec80c,
    sec80d,
    hra,
    homeLoanInterest,
    otherDeductions,
  };

  const oldResult = useMemo(() => calcRegime({ fy, regime: "OLD", inputs }), [
    fy, employmentType, grossIncome, ageGroup, employerNps, sec80c, sec80d, hra, homeLoanInterest, otherDeductions,
  ]);
  const newResult = useMemo(() => calcRegime({ fy, regime: "NEW", inputs }), [
    fy, employmentType, grossIncome, ageGroup, employerNps,
  ]);

  const betterRegime = oldResult.totalTax <= newResult.totalTax ? "OLD" : "NEW";
  const savings = Math.abs(oldResult.totalTax - newResult.totalTax);
  const zeroTaxNewRegime = fy === "FY2025-26" && newResult.totalTax === 0 && grossIncome > 0;

  return (
    <>
      <Helmet>
        <title>Income Tax Calculator — Old vs New Regime (FY 2024-25 & FY 2025-26) | WINTRUST SOLUTIONS</title>
        <meta name="description" content="Compare Old vs New tax regime for FY 2024-25 and FY 2025-26. Free income tax calculator with Budget 2025 slabs, 87A rebate, and deductions. Expert tax planning in Coimbatore." />
      </Helmet>

      <section className="bg-navy py-12 md:py-16">
        <div className="container-custom">
          <nav className="text-slate-400 text-sm mb-4">
            <Link to="/" className="hover:text-gold">Home</Link> / <span className="text-white">Income Tax Calculator</span>
          </nav>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white font-heading leading-tight mb-4" data-testid="income-tax-h1">
            Income Tax Calculator — Old vs New Regime (FY 2024-25 & FY 2025-26)
          </h1>
          <p className="text-slate-300 text-lg max-w-3xl">
            Instantly compare your income tax liability under Old and New tax regimes. Includes updated FY 2025-26 slabs from Union Budget 2025 with the enhanced ₹60,000 rebate under Section 87A.
          </p>
        </div>
      </section>

      <section className="bg-slate-50 section-padding">
        <div className="container-custom">
          {/* FY Toggle */}
          <div className="flex justify-center mb-10">
            <div className="inline-flex bg-white rounded-xl border border-slate-200 p-1 shadow-sm" data-testid="fy-toggle">
              <button
                onClick={() => setFy("FY2024-25")}
                className={`px-4 md:px-6 py-2.5 rounded-lg text-sm font-semibold transition-all ${fy === "FY2024-25" ? "bg-navy text-white" : "text-navy hover:text-gold"}`}
                data-testid="fy-2024-btn"
              >
                FY 2024-25 (AY 2025-26)
              </button>
              <button
                onClick={() => setFy("FY2025-26")}
                className={`px-4 md:px-6 py-2.5 rounded-lg text-sm font-semibold transition-all ${fy === "FY2025-26" ? "bg-navy text-white" : "text-navy hover:text-gold"}`}
                data-testid="fy-2025-btn"
              >
                FY 2025-26 (AY 2026-27)
              </button>
            </div>
          </div>

          {/* Inputs */}
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 md:p-8 border border-slate-200 shadow-sm">
              <h2 className="text-xl font-semibold text-navy mb-5 font-heading">Your Income Details</h2>

              <div className="mb-4">
                <label className="text-navy font-semibold mb-2 block text-sm">Employment Type</label>
                <select
                  value={employmentType}
                  onChange={(e) => setEmploymentType(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 text-navy text-sm font-medium focus:outline-none focus:border-gold"
                  data-testid="employment-type-select"
                >
                  <option value="SALARIED">Salaried</option>
                  <option value="SELF_EMPLOYED">Self-Employed</option>
                  <option value="FREELANCER">Freelancer / Professional</option>
                </select>
              </div>

              <div className="mb-4">
                <label className="text-navy font-semibold mb-2 flex items-center gap-2 text-sm">
                  <IndianRupee size={14} /> Annual Gross Income
                </label>
                <input
                  type="number"
                  min={0}
                  value={grossIncome}
                  onChange={(e) => setGrossIncome(Math.max(0, Number(e.target.value)))}
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 text-navy text-sm font-medium focus:outline-none focus:border-gold"
                  data-testid="gross-income-input"
                />
              </div>

              <div className="mb-4">
                <label className="text-navy font-semibold mb-2 block text-sm">Age Group</label>
                <select
                  value={ageGroup}
                  onChange={(e) => setAgeGroup(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 text-navy text-sm font-medium focus:outline-none focus:border-gold"
                  data-testid="age-group-select"
                >
                  <option value="BELOW_60">Below 60</option>
                  <option value="SENIOR">60–80 (Senior Citizen)</option>
                  <option value="SUPER_SENIOR">Above 80 (Super Senior)</option>
                </select>
              </div>

              <div>
                <label className="text-navy font-semibold mb-2 block text-sm">Employer NPS Contribution — 80CCD(2)</label>
                <input
                  type="number"
                  min={0}
                  value={employerNps}
                  onChange={(e) => setEmployerNps(Math.max(0, Number(e.target.value)))}
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 text-navy text-sm font-medium focus:outline-none focus:border-gold"
                  data-testid="nps-input"
                />
                <p className="text-xs text-slate-500 mt-1">Allowed in both regimes — up to 14% of basic salary.</p>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 md:p-8 border border-slate-200 shadow-sm">
              <h2 className="text-xl font-semibold text-navy mb-1 font-heading">Old Regime Deductions</h2>
              <p className="text-xs text-slate-500 mb-5">Not applicable in New Regime.</p>

              <div className="grid grid-cols-1 gap-3">
                <div>
                  <label className="text-navy font-semibold mb-1 block text-sm">80C Investments (max ₹1,50,000)</label>
                  <input
                    type="number" min={0}
                    value={sec80c}
                    onChange={(e) => setSec80c(Math.max(0, Number(e.target.value)))}
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 text-navy text-sm focus:outline-none focus:border-gold"
                    data-testid="sec80c-input"
                  />
                </div>
                <div>
                  <label className="text-navy font-semibold mb-1 block text-sm">80D Health Insurance (max ₹25K / ₹50K senior)</label>
                  <input
                    type="number" min={0}
                    value={sec80d}
                    onChange={(e) => setSec80d(Math.max(0, Number(e.target.value)))}
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 text-navy text-sm focus:outline-none focus:border-gold"
                    data-testid="sec80d-input"
                  />
                </div>
                <div>
                  <label className="text-navy font-semibold mb-1 block text-sm">HRA Exemption</label>
                  <input
                    type="number" min={0}
                    value={hra}
                    onChange={(e) => setHra(Math.max(0, Number(e.target.value)))}
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 text-navy text-sm focus:outline-none focus:border-gold"
                    data-testid="hra-input"
                  />
                </div>
                <div>
                  <label className="text-navy font-semibold mb-1 block text-sm">Home Loan Interest — Section 24b (max ₹2,00,000)</label>
                  <input
                    type="number" min={0}
                    value={homeLoanInterest}
                    onChange={(e) => setHomeLoanInterest(Math.max(0, Number(e.target.value)))}
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 text-navy text-sm focus:outline-none focus:border-gold"
                    data-testid="home-loan-input"
                  />
                </div>
                <div>
                  <label className="text-navy font-semibold mb-1 block text-sm">Other Deductions</label>
                  <input
                    type="number" min={0}
                    value={otherDeductions}
                    onChange={(e) => setOtherDeductions(Math.max(0, Number(e.target.value)))}
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 text-navy text-sm focus:outline-none focus:border-gold"
                    data-testid="other-deductions-input"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Results */}
          <div className="mt-10" data-testid="income-tax-results">
            {zeroTaxNewRegime && (
              <div className="bg-green-50 border-l-4 border-green-500 rounded-lg p-5 mb-6 flex items-center gap-3" data-testid="zero-tax-box">
                <CheckCircle2 className="text-green-600 flex-shrink-0" size={24} />
                <p className="text-green-900 font-semibold">
                  ✓ ZERO TAX under New Regime for FY 2025-26
                </p>
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-6">
              {/* Old Regime */}
              <div
                className={`bg-white rounded-xl p-6 border-2 ${betterRegime === "OLD" ? "border-gold shadow-lg" : "border-slate-200"}`}
                data-testid="old-regime-card"
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-navy font-heading">Old Regime</h3>
                  {betterRegime === "OLD" && (
                    <span className="text-xs font-bold text-gold bg-amber-50 px-2 py-1 rounded">RECOMMENDED</span>
                  )}
                </div>
                <div className="space-y-2 text-sm">
                  <Row label="Gross Income" value={oldResult.grossIncome} />
                  <Row label="Total Deductions" value={oldResult.deductions} />
                  <Row label="Taxable Income" value={oldResult.taxableIncome} bold />
                  <Row label="Tax Before Cess" value={oldResult.baseTax} />
                  <Row label="Rebate 87A" value={oldResult.rebate} />
                  <Row label="Cess (4%)" value={oldResult.cess} />
                  <div className="h-px bg-slate-200 my-2" />
                  <div className="flex justify-between items-center py-2 bg-navy text-white rounded-lg px-3">
                    <span className="font-semibold">Total Tax Payable</span>
                    <span className="font-bold text-gold" data-testid="old-regime-tax">₹{formatINR(oldResult.totalTax)}</span>
                  </div>
                </div>
              </div>

              {/* New Regime */}
              <div
                className={`bg-white rounded-xl p-6 border-2 ${betterRegime === "NEW" ? "border-gold shadow-lg" : "border-slate-200"}`}
                data-testid="new-regime-card"
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-navy font-heading">New Regime</h3>
                  {betterRegime === "NEW" && (
                    <span className="text-xs font-bold text-gold bg-amber-50 px-2 py-1 rounded">RECOMMENDED</span>
                  )}
                </div>
                <div className="space-y-2 text-sm">
                  <Row label="Gross Income" value={newResult.grossIncome} />
                  <Row label="Total Deductions" value={newResult.deductions} />
                  <Row label="Taxable Income" value={newResult.taxableIncome} bold />
                  <Row label="Tax Before Cess" value={newResult.baseTax} />
                  <Row label="Rebate 87A" value={newResult.rebate} />
                  <Row label="Cess (4%)" value={newResult.cess} />
                  <div className="h-px bg-slate-200 my-2" />
                  <div className="flex justify-between items-center py-2 bg-navy text-white rounded-lg px-3">
                    <span className="font-semibold">Total Tax Payable</span>
                    <span className="font-bold text-gold" data-testid="new-regime-tax">₹{formatINR(newResult.totalTax)}</span>
                  </div>
                </div>
              </div>
            </div>

            {savings > 0 && (
              <div className="mt-6 bg-navy rounded-xl p-6 text-center" data-testid="savings-box">
                <p className="text-gold text-lg md:text-xl font-semibold">
                  ✓ RECOMMENDED — You save ₹{formatINR(savings)} under {betterRegime === "OLD" ? "Old" : "New"} Regime
                </p>
              </div>
            )}
          </div>

          {/* Amber Alert */}
          <div className="mt-10 bg-amber-50 border-l-4 border-amber-500 rounded-lg p-6 md:p-8" data-testid="amber-alert">
            <div className="flex items-start gap-4">
              <AlertTriangle className="text-amber-600 flex-shrink-0 mt-1" size={28} />
              <div className="flex-1">
                <p className="text-amber-900 font-semibold text-base md:text-lg mb-4">
                  Want to legally minimise your tax? Our experts will review your complete income profile and plan the best regime for you.
                </p>
                <div className="flex flex-wrap gap-3 items-center">
                  <a
                    href={WHATSAPP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-whatsapp"
                    data-testid="cta-whatsapp-btn"
                  >
                    <MessageCircle size={18} /> Get Tax Planning — WhatsApp Us
                  </a>
                  <Link
                    to="/itr-filing"
                    className="text-navy font-semibold hover:text-gold transition-colors flex items-center gap-1"
                    data-testid="link-itr-filing"
                  >
                    View ITR Filing Services <ChevronRight size={16} />
                  </Link>
                </div>
              </div>
            </div>
          </div>

          <p className="mt-8 text-xs text-slate-500 max-w-3xl">
            <strong>Disclaimer:</strong> Covers FY 2024-25 (AY 2025-26) and FY 2025-26 (AY 2026-27). New Tax Regime is default from FY 2024-25. FY 2025-26 slabs per Union Budget 2025. Actual liability may vary. Consult a tax expert.
          </p>
        </div>
      </section>
    </>
  );
};

const Row = ({ label, value, bold = false }) => (
  <div className="flex justify-between">
    <span className="text-slate-600">{label}</span>
    <span className={`${bold ? "font-semibold text-navy" : "text-navy"}`}>₹{formatINR(value)}</span>
  </div>
);

export default IncomeTaxCalculatorPage;
